import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ERROR_MESSAGE, LOCAL_STORAGE_KEY, TOKEN_API_ENDPOINT, ROUTER_CONFIG } from '@core/configs';
import { ConstantOfTriggerSession, KeyTriggerSession, LocalStorageHelper, LocalStorageKey } from '@core/helper/local-storage.helper';
import { ApiResponse } from '@core/models/interfaces';
import { ApiService, AutoLogoutService, HttpCancelService, LocalStorageService, RequestService } from '@core/services/common';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { AuthConsoleLoginRO } from '@core/models/ro';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  jwtHelperService: JwtHelperService = new JwtHelperService();
  private hasQueryTokenSubject = new BehaviorSubject<boolean>(false);
  hasQueryToken = this.hasQueryTokenSubject.asObservable();

  constructor(
    private readonly http: ApiService,
    private router: Router,
    private autoLogoutService: AutoLogoutService,
    private cancelService: HttpCancelService,
    private requestService: RequestService,
    private localStorageService: LocalStorageService,
    // private cookieService: CookieService
  ) { }

  setHasQueryToken(val: boolean) {
    this.hasQueryTokenSubject.next(val);
  }

  get isAccessTokenExpired(): boolean {
    const accessToken = this.localStorageService.getAccessToken();
    if (accessToken) {
      return this.jwtHelperService.isTokenExpired(accessToken);
    }
    return true;
  }

  get isRefreshTokenExpired(): boolean {
    const refreshToken = this.localStorageService.getRefreshToken();
    if (refreshToken) {
      return this.jwtHelperService.isTokenExpired(refreshToken);
    }
    return true;
  }

  loggedIn() {
    if (this.isRefreshTokenExpired) {
      if (this.localStorageService.getItem(LOCAL_STORAGE_KEY.userSession)) {
        this.logout();
      }
      return false;
    } else {
      return true;
    }
  }

  getModuleByToken(token: string): string {
    return '';
  }

  async loginByToken(params: { accessToken: string; refreshToken: string }) {
    const decodeToken = this.jwtHelperService.decodeToken(params.accessToken);

    if (!decodeToken || !decodeToken.userId) {
      throw new Error('Invalid token!');
    }

    const userSessionLocal = LocalStorageHelper.getUserSession();
    const userSessionObject: AuthConsoleLoginRO = {
      accessToken: params.accessToken,
      refreshToken: params.refreshToken,
    };

    if (userSessionLocal && userSessionLocal.userId) {
      const isCurrentUser = this.isCurrentUser(decodeToken.userId);
      if (isCurrentUser) {
        sessionStorage.setItem(LocalStorageKey.SESSION_LOGIN, LocalStorageKey.SESSION_LOGIN);
        LocalStorageHelper.setUserSession(userSessionObject);
        LocalStorageHelper.trigger(ConstantOfTriggerSession.SET_TOKEN);
      } else {
        // Logout and login with the accessToken passed in
        this.logout(false).then(() => {
          sessionStorage.setItem(LocalStorageKey.SESSION_LOGIN, LocalStorageKey.SESSION_LOGIN);
          LocalStorageHelper.setUserSession(userSessionObject);
          LocalStorageHelper.trigger(ConstantOfTriggerSession.SET_TOKEN);
        });
      }
    } else {
      sessionStorage.setItem(LocalStorageKey.SESSION_LOGIN, LocalStorageKey.SESSION_LOGIN);
      LocalStorageHelper.setUserSession(userSessionObject);
      LocalStorageHelper.trigger(ConstantOfTriggerSession.SET_TOKEN);
    }
  }

  requestAccessToken(refreshToken: string): Observable<ApiResponse<any>> {
    if (refreshToken) {
      return this.http.post(TOKEN_API_ENDPOINT.refreshToken, {
        refreshToken,
      });
    } else {
      return throwError(ERROR_MESSAGE.refreshTokenNotFound);
    }
  }

  async logout(isNavigateToLogin: boolean = true, isLog = false) {
    if (!this.requestService.checkCompletedRequests()) {
      this.cancelService.cancelPendingRequest();
    }

    if (!this.isAccessTokenExpired || !this.isRefreshTokenExpired) {
      this.callLogout()
        .toPromise()
        .catch(() => {
          this.callLogout();
        });
    }
    this.autoLogoutService.stop();
    this.localStorageService.clear();
    sessionStorage.clear();
    this.localStorageService.trigger(KeyTriggerSession.REMOVE_TOKEN);
    if (isNavigateToLogin) {
      this.router.navigate([ROUTER_CONFIG.auth.login]);
    }
  }

  private callLogout() {
    return this.http.delete(TOKEN_API_ENDPOINT.logout);
  }

  private isCurrentUser(userId: string) {
    const currentAccessToken = '';
    const decodeCurrentToken = { userId: '' };
    if (!userId) {
      throw new Error('Invalid token!');
    }
    return Number(decodeCurrentToken?.userId) === Number(userId);
  }

  verifyRefreshToken(refreshToken: string) {
    return this.http.post(TOKEN_API_ENDPOINT.verifyRefreshToken, { refreshToken });
  }
}
